/**
 * Contrast-safe color primitive.
 *
 * Survey group colors are per-survey CONTENT, not design-system tokens: any author
 * (or API caller) can set a group's color, and older templates store bare CSS color
 * names like "yellow" (= #FFFF00). The style guide can't fix those, so this module
 * is the single place that guarantees a readable result for ANY incoming color.
 *
 * Use `safeFill(color)` wherever a dynamic/author-chosen color becomes a filled
 * background that needs a label on top. It resolves names to hex, picks the readable
 * text color by luminance, and darkens the fill if neither black nor white can reach
 * WCAG AA. Use `resolveColor` when you only need the hex (e.g. a swatch with no text).
 */

export const FOREGROUND_DARK = '#1F2937';
export const AA_NORMAL_TEXT = 4.5;

// Legacy CSS color names used by seeded survey templates → on-brand, AA-friendly hex.
// "yellow" deliberately maps to a readable amber, never the raw #FFFF00.
const NAME_TO_HEX: Record<string, string> = {
  red: '#DC2626',
  pink: '#EC4899',
  orange: '#F97316',
  yellow: '#F59E0B',
  amber: '#F59E0B',
  green: '#22C55E',
  blue: '#2563EB',
  indigo: '#4F46E5',
  purple: '#7C5CBF',
  gray: '#6B7280',
  grey: '#6B7280',
};

const DEFAULT_FALLBACK = '#4F46E5';

function normalizeHex(hex: string): string {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return '#' + h.slice(0, 6).toUpperCase();
}

/** Resolve a name or hex to a normalized hex. Unknown input returns the fallback. */
export function resolveColor(input?: string | null, fallback: string = DEFAULT_FALLBACK): string {
  if (!input) return fallback;
  const v = input.trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return normalizeHex(v);
  return NAME_TO_HEX[v.toLowerCase()] ?? fallback;
}

const channel = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

export function relativeLuminance(hex: string): number {
  const n = parseInt(normalizeHex(hex).slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** White or dark foreground, whichever reads better on `bg`. */
export function readableTextColor(bg: string): string {
  return contrastRatio('#FFFFFF', bg) >= contrastRatio(FOREGROUND_DARK, bg) ? '#FFFFFF' : FOREGROUND_DARK;
}

function darken(hex: string, factor: number): string {
  const n = parseInt(normalizeHex(hex).slice(1), 16);
  const r = Math.round(((n >> 16) & 255) * factor);
  const g = Math.round(((n >> 8) & 255) * factor);
  const b = Math.round((n & 255) * factor);
  return '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('').toUpperCase();
}

export interface SafeFill {
  /** AA-safe background hex (may be darkened from the input for legibility). */
  background: string;
  /** Readable text/icon color for that background (`#FFFFFF` or the dark foreground). */
  text: string;
}

/**
 * Turn any color into a guaranteed-readable {background, text} pair.
 * Picks the better text color; if neither reaches `threshold`, progressively
 * darkens the fill until white text passes. Always returns an AA-safe pair.
 */
export function safeFill(
  input?: string | null,
  opts: { threshold?: number; fallback?: string } = {},
): SafeFill {
  const threshold = opts.threshold ?? AA_NORMAL_TEXT;
  let background = resolveColor(input, opts.fallback ?? DEFAULT_FALLBACK);
  const text = readableTextColor(background);
  if (contrastRatio(text, background) >= threshold) {
    return { background, text };
  }
  // Mid-tone fill that neither black nor white can sit on at AA: darken toward
  // a shade where white text passes (white wins as a fill gets darker).
  let guard = 0;
  while (contrastRatio('#FFFFFF', background) < threshold && guard++ < 24) {
    background = darken(background, 0.9);
  }
  return { background, text: '#FFFFFF' };
}

/**
 * Single source of truth for the survey group color palette. Every swatch yields a
 * readable pair through `safeFill`. The survey builder and the style guide both read
 * from here, so the picker can never drift from what the renderer guarantees.
 */
export const SURVEY_GROUP_PALETTE: ReadonlyArray<{ value: string; label: string }> = [
  { value: '#4F46E5', label: 'Indigo' },
  { value: '#6EE7B7', label: 'Mint' },
  { value: '#FBBF24', label: 'Amber' },
  { value: '#2563EB', label: 'Blue' },
  { value: '#22C55E', label: 'Green' },
  { value: '#DC2626', label: 'Red' },
  { value: '#7C5CBF', label: 'Purple' },
  { value: '#3B3D93', label: 'Dark Indigo' },
];
